<template>
    <div class="record-create">
        <b-alert v-if="isLoadingFirmData" :show="true" variant="info">
            Lade Firmendaten ...
        </b-alert>

        <b-form v-if="shouldShowForm" class="record-create" @submit.prevent="submit">
            <div class="record-creation-wrapper">
                <div role="tablist">
                    <csp-foldable collapse-id="intro" link-to-section="base" route-name="record-create"
                        title="Allgemeine Informationen">
                        <div class="collapse-body p-5">
                            <h2>
                                Willkommen bei <span class="text-tertiary">
                                    {{ selectedFirmNameWithPlace ? selectedFirmNameWithPlace : ' Ihrem Autohaus' }}
                                </span>
                            </h2>
                            <template v-if="shouldShowPersonalGreeting">
                                <p>Liebe Kundin, lieber Kunde,</p>
                                <p>
                                    wir freuen uns sehr über das uns entgegengebrachte Vertrauen im Zusammenhang mit
                                    der Bearbeitung Ihres Unfallschadens.
                                    Bitte geben Sie nun alle Ihnen bekannten Daten ein.
                                </p>
                                <p>
                                    Sollten Sie Fragen zur Aufnahme der Daten haben, stehen wir Ihnen telefonisch gern
                                    {{ selectedFirmPhone ? ' unter ' + selectedFirmPhone : '' }} zur Verfügung.
                                </p>
                                <p>
                                    Freundliche Grüße<br>Ihr
                                    {{ selectedFirmNameWithPlace ? selectedFirmNameWithPlace : ' Autohaus' }}
                                </p>
                            </template>
                            <template v-else-if="isFleetOrMajorCustomer">
                                <p>
                                    Liebe Fahrzeugnutzerin, Lieber Fahrzeugnutzer,
                                </p>
                                <p>
                                    wir bedauern sehr, dass es zu einem Unfall mit Ihrem Fahrzeug gekommen ist.
                                    In Kürze wird sich eine unserer Mitarbeiterinnen / einer unserer Mitarbeiter um den
                                    Vorgang kümmern. Um die nun erforderlichen Aktivitäten schnellstmöglich beginnen
                                    zu können, bitten wir Sie, uns bei der Datenaufnahme zu unterstützen.
                                </p>
                                <p>
                                    Bitte beantworten Sie die folgenden Fragen. Sollten Sie etwas nicht wissen,
                                    lassen Sie das Feld gern frei.
                                </p>
                                <p>
                                    Herzlichen Dank für Ihre Hilfe und los geht ́s...
                                </p>
                            </template>
                            <template v-else>
                                <p>Liebe Kundin, lieber Kunde,</p>
                                <p>
                                    in wenigen Minuten wird sich eine unserer Mitarbeiterinnen / einer unserer
                                    Mitarbeiter persönlich um Sie kümmern.
                                    Um Ihnen die Wartezeit zu verkürzen, bitten wir Sie, uns bei der Datenaufnahme zu
                                    unterstützen.
                                    Bitte beantworten Sie die folgenden Fragen.
                                    Sollten Sie etwas nicht wissen, lassen Sie das Feld gerne frei.
                                </p>
                                <p>Herzlichen Dank für Ihre Hilfe und los geht´s...</p>
                            </template>
                        </div>
                    </csp-foldable>

                    <csp-foldable collapse-id="base"
                        :link-classes="stateClasses('/((claimType|damage)/|firmId)')"
                        link-to-section="customer" route-name="record-create"
                        :route-params="{}" title="Schritt 1 - Unfalldaten">
                        <csp-record-create-base-by-customer
                            :record-form="recordForm"/>
                    </csp-foldable>

                    <csp-foldable collapse-id="customer" :link-classes="stateClasses('/customer/')"
                        link-to-section="vehicle" route-name="record-create"
                        :route-params="{}" title="Schritt 2 - Persönliche Daten">
                        <csp-record-create-customer :is-fleet-or-major-customer="isFleetOrMajorCustomer"
                            :record-form="recordForm"/>
                    </csp-foldable>

                    <csp-foldable collapse-id="vehicle" :link-classes="stateClasses('/vehicle/')"
                        :link-to-section="vehicleSectionLinkTo" route-name="record-create"
                        :route-params="{}" title="Schritt 3 - Fahrzeugdaten">
                        <div class="collapse-body mb-0">
                            <csp-create-vehicle-form
                                :is-comprehensive-claim-type-selected="isComprehensiveClaimTypeSelected"
                                :upload-session-id="uploadSessionId"
                                :vehicle-form="recordForm.getSubformVehicle()"/>
                        </div>
                    </csp-foldable>

                    <csp-foldable v-if="!isComprehensiveClaimTypeSelected" collapse-id="adversary"
                        :link-classes="stateClasses('/adversary/')" link-to-section="completion"
                        route-name="record-create" :route-params="{}" title="Schritt 4 - Daten Unfallgegner">
                        <div class="collapse-body pb-0">
                            <csp-create-adversary-form :record-form="recordForm"/>
                        </div>
                    </csp-foldable>

                    <csp-foldable v-if="isComprehensiveClaimTypeSelected" collapse-id="comprehensive-insurance"
                        :link-classes="stateClasses('/comprehensiveInsurancePolicy/')"
                        link-to-section="completion" route-name="record-create"
                        :route-params="{}" title="Schritt 4 - Daten Versicherung">
                        <div class="collapse-body pb-0">
                            <csp-create-insurance-policy-form
                                :insurance-policy-form="recordForm.getSubform('comprehensiveInsurancePolicy')"/>
                        </div>
                    </csp-foldable>

                    <csp-foldable collapse-id="completion" route-name="record-create" title="Abschluss">
                        <div class="collapse-body p-5">
                            <h2>Ganz herzlichen Dank für Ihre Unterstützung.</h2>
                            <p v-if="!isFleetOrMajorCustomer">
                                {{ customerLabel }},
                            </p>
                            <p>
                                Hinterlassen Sie uns gerne weitere Informationen im nachfolgenden Feld oder rechts
                                per Foto-Upload.
                            </p>
                            <div class="row">
                                <div class="col-9">
                                    <csp-form-group-textarea :form="recordForm" label-property="furtherRemarks"
                                        property="note"/>
                                </div>
                                <div class="col-3 align-self-center">
                                    <csp-record-create-image-file-upload
                                        accepted-mime-types="image/jpeg, image/png, application/pdf"
                                        transform-mime-types="image/jpeg, image/png"
                                        :upload-file-category-id="miscUploadFileCategory"
                                        :upload-session-id="uploadSessionId"/>
                                </div>
                            </div>
                            <p>
                                Bitte klicken Sie nun unten rechts auf
                                <span class="text-primary text-uppercase font-weight-bold">SPEICHERN</span>.
                                Sollte es noch Rückfragen geben, wird sich eine Mitarbeiterin / ein Mitarbeiter
                                von uns mit Ihnen in Verbindung setzen. Falls bereits ein Vor-Ort Termin in der
                                Werkstatt vereinbart wurde, werden alle weiteren Daten dann direkt bei uns ergänzt.
                            </p>
                        </div>
                    </csp-foldable>
                </div>

                <div class="record-creation-footer footer text-center">
                    <div class="row">
                        <div class="col-12">
                            <div class="text-right">
                                <csp-form-group-checkbox :form="recordForm" :hide-label="true"
                                    property="isPrivacyPolicyAcknowledged">
                                    <template v-slot:label>
                                        Ich habe die
                                        <a href="https://autoclaim.de/datenschutz/" rel="noopener noreferrer"
                                            target="_blank">Datenschutzerklärung</a>
                                        zur Kenntnis genommen
                                    </template>
                                </csp-form-group-checkbox>
                                <b-button class="btn-action" :disabled="isCreating" type="submit" variant="primary">
                                    <span v-if="isCreating" aria-hidden="true"
                                        class="spinner-border spinner-border-sm" role="status"/>
                                    Speichern
                                </b-button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </b-form>

        <b-alert v-if="shouldShowMissingFirmsWarning" :show="true" variant="warning">
            Es stehen keine Firmen zur Verfügung, für die Akten angelegt werden können!
        </b-alert>

        <b-alert v-if="isLoadingFirms" :show="true" variant="info">
            Lade Filialdaten ...
        </b-alert>
    </div>
</template>

<script>
    import CreateVehicleByCustomerForm from '../recordByCustomer/form/CreateVehicleForm';
    import GenderEnum from '@js/enums/GenderEnum';
    import RecordCreateBaseByCustomer from './../recordByCustomer/create/Base';
    import RecordCreateCustomerByCustomer from './../recordByCustomer/create/Customer';
    import RecordCreateMixin from '@components/record/RecordCreateMixin';
    import FileCategoryEnum from '@js/enums/file/FileCategoryEnum';

    export default {
        components: {
            'csp-record-create-base-by-customer': RecordCreateBaseByCustomer,
            'csp-record-create-customer': RecordCreateCustomerByCustomer,
            'csp-create-vehicle-form': CreateVehicleByCustomerForm,
        },
        mixins: [RecordCreateMixin],
        data() {
            return {
                selectedFirmNameWithPlace: '',
                selectedFirmPhone: '',
                selectedFirmIsFleetCustomer: false,
                selectedFirmIsMajorCustomer: false,
            }
        },
        computed: {
            customerLabel() {
                const customerAttributes = this.recordAttributes.customer.data.attributes;
                if (customerAttributes.gender === GenderEnum.MALE) {
                    return 'Lieber ' + customerAttributes.firstName + ' ' + customerAttributes.lastName;
                }

                if (customerAttributes.gender === GenderEnum.FEMALE) {
                    return 'Liebe ' + customerAttributes.firstName + ' ' + customerAttributes.lastName
                }

                return 'Lieber Kunde';
            },
            miscUploadFileCategory() {
                return FileCategoryEnum.OTHER_DOCUMENT;
            },
            shouldShowForm() {
                return !this.isLoadingFirms && this.firmOptions.length > 0;
            },
            shouldShowPersonalGreeting() {
                let uri = window.location.search.substring(1);
                let params = new URLSearchParams(uri);
                return parseInt(params.get('showPersonalGreeting'));
            },
            isFleetOrMajorCustomer() {
                return this.selectedFirmIsFleetCustomer || this.selectedFirmIsMajorCustomer;
            },
            uploadSessionId() {
                return this.recordForm.getDataOfProperty('uploadSessionId');
            },
            vehicleSectionLinkTo() {
                if (this.isComprehensiveClaimTypeSelected) {
                    return 'comprehensive-insurance';
                }

                return 'adversary';
            },
        },
        mounted() {
            this.recordForm.setRecordCreationByCustomer(true);
            // this.recordForm.minimalRecordCreationEnable() will
            // be called asynchronously by a watcher from RecordCreateMixin.
            this.recordForm.forceMinimalRecordCreation();
        },
        methods: {
            initFirmData(firmData) {
                const firmAttributes = firmData.data.attributes;
                this.selectedFirmNameWithPlace = firmAttributes.nameWithPlace;
                this.selectedFirmPhone = firmAttributes.phone;
                this.selectedFirmIsFleetCustomer = firmAttributes.isFleetCustomer;
                this.selectedFirmIsMajorCustomer = firmAttributes.isMajorCustomer;

                if (firmAttributes.isFleetCustomer === true
                    || firmAttributes.isMajorCustomer === true) {
                    this.recordForm.setIsFleetOrMajorCustomer(true);

                    const customerForm = this.recordForm.getSubform('customer');
                    customerForm.setProperty('gender', GenderEnum.COMPANY);

                    if (firmAttributes.isMajorCustomer === true) {
                        customerForm.setProperty('companyName', firmAttributes.name);
                        customerForm.setProperty('street', firmAttributes.street);
                        customerForm.setProperty('postalCode', firmAttributes.postalCode);
                        customerForm.setProperty('place', firmAttributes.city);
                    }
                }
            },
        },
    }
</script>
